import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Customer, PagedResult } from '../types/api';

const CUSTOMERS_KEY = 'customers';

export function useCustomers(pageNumber = 1, pageSize = 10, search = '') {
  return useQuery<PagedResult<Customer>>({
    queryKey: [CUSTOMERS_KEY, pageNumber, pageSize, search],
    queryFn: async () => {
      const params: any = { pageNumber, pageSize };
      if (search) params.search = search;

      const response = await api.get<any>('/customers', { params });
      
      const apiResult = response.data?.data || response.data || { items: [], totalCount: 0 };
      const itemsArray = Array.isArray(apiResult.items) ? apiResult.items : (Array.isArray(apiResult) ? apiResult : []);
      const totalCount = apiResult.totalCount || itemsArray.length;

      const normalizedItems = itemsArray.map((item: any) => ({
        ...item,
        name: item.name || item.fullName || 'عميل غير مسمى'
      }));

      return {
        items: normalizedItems,
        totalPages: Math.ceil(totalCount / pageSize) || 1,
        totalCount: totalCount,
        pageNumber,
        pageSize,
        hasPreviousPage: pageNumber > 1,
        hasNextPage: pageNumber * pageSize < totalCount
      } as any;
    },
  });
}

export function useCustomer(id: number) {
  return useQuery<Customer>({
    queryKey: [CUSTOMERS_KEY, id],
    queryFn: async () => {
      const response = await api.get<any>(`/customers/${id}`);
      const item = response.data?.data || response.data;
      if (item) {
        item.name = item.name || item.fullName;
      }
      return item;
    },
    enabled: !!id,
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      // صمام أمان: أخذ الاسم سواء كان قادماً من الفورم باسم name أو fullName
      const payload = {
        name: data.name || data.fullName || '',
        phone: data.phone,
        email: data.email || '',
        address: data.address || '',
        city: data.city || 'القاهرة',
        area: data.area || 'عام',
        isActive: true
      };
      const response = await api.post<any>('/customers', payload);
      return response.data?.data || response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CUSTOMERS_KEY] }),
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const payload = {
        id,
        name: data.name || data.fullName || '',
        phone: data.phone,
        email: data.email,
        address: data.address,
        city: data.city,
        area: data.area,
        isActive: data.isActive
      };
      const response = await api.put<any>(`/customers/${id}`, payload);
      return response.data?.data || response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CUSTOMERS_KEY] }),
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.delete<any>(`/customers/${id}`);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CUSTOMERS_KEY] }),
  });
}

export function useSearchCustomers(search: string) {
  return useQuery<Customer[]>({
    queryKey: [CUSTOMERS_KEY, 'search', search],
    queryFn: async () => {
      const response = await api.get<any>('/customers', { params: { search, pageSize: 50 } });
      const apiResult = response.data?.data || response.data || [];
      const itemsArray = Array.isArray(apiResult.items) ? apiResult.items : (Array.isArray(apiResult) ? apiResult : []);
      
      return itemsArray.map((item: any) => ({
        ...item,
        name: item.name || item.fullName
      }));
    },
    enabled: search.length >= 2,
  });
}
